import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const FORM_SELECTOR =
    '[data-map-suggestion-form]';

function parseCoordinate(value) {
    const parsed = Number.parseFloat(value);

    return Number.isFinite(parsed)
        ? parsed
        : null;
}

function parseBoundary(value) {
    try {
        const parsed = JSON.parse(
            value || '[]'
        );

        if (!Array.isArray(parsed)) {
            return [];
        }

        return parsed
            .map((point) => {
                const latitude =
                    parseCoordinate(
                        point?.latitude
                    );

                const longitude =
                    parseCoordinate(
                        point?.longitude
                    );

                if (
                    latitude === null
                    || longitude === null
                ) {
                    return null;
                }

                return {
                    latitude,
                    longitude,
                };
            })
            .filter(Boolean);
    } catch {
        return [];
    }
}

function initializeForm(form) {
    if (
        form.dataset
            .mapSuggestionZoneBoundaryReady
        === 'true'
    ) {
        return;
    }

    const typeSelect = form.querySelector(
        '[data-map-suggestion-type]'
    );

    const section = form.querySelector(
        '[data-map-suggestion-zone-boundary-section]'
    );

    const picker = form.querySelector(
        '[data-map-suggestion-zone-picker]'
    );

    const mapElement = form.querySelector(
        '[data-map-suggestion-zone-map]'
    );

    const boundaryInput = form.querySelector(
        '[data-map-suggestion-zone-boundary]'
    );

    const pointCount = form.querySelector(
        '[data-map-suggestion-zone-point-count]'
    );

    const status = form.querySelector(
        '[data-map-suggestion-zone-status]'
    );

    const undoButton = form.querySelector(
        '[data-map-suggestion-zone-undo]'
    );

    const clearButton = form.querySelector(
        '[data-map-suggestion-zone-clear]'
    );

    if (
        !typeSelect
        || !section
        || !picker
        || !mapElement
        || !boundaryInput
    ) {
        return;
    }

    form.dataset
        .mapSuggestionZoneBoundaryReady =
            'true';

    let map = null;
    let polygon = null;
    let pointMarkers = [];
    let points = parseBoundary(
        boundaryInput.value
    );

    const syncInput = () => {
        boundaryInput.value =
            JSON.stringify(
                points.map((point) => ({
                    latitude: Number(
                        point.latitude.toFixed(7)
                    ),
                    longitude: Number(
                        point.longitude.toFixed(7)
                    ),
                }))
            );
    };

    const updateStatus = () => {
        const count = points.length;

        if (pointCount) {
            pointCount.textContent =
                `${count} ${count === 1 ? 'point' : 'points'}`;
        }

        undoButton?.toggleAttribute(
            'disabled',
            count === 0
        );

        clearButton?.toggleAttribute(
            'disabled',
            count === 0
        );

        if (!status) {
            return;
        }

        if (count === 0) {
            status.textContent =
                typeSelect.value === 'new_zone'
                    ? 'Click around the edge of the new zone to begin drawing.'
                    : 'Click around the corrected edge of the zone to begin drawing.';
        } else if (count < 3) {
            status.textContent =
                `Add ${3 - count} more ${3 - count === 1 ? 'point' : 'points'} to form a proposed boundary.`;
        } else {
            status.textContent =
                'Proposed boundary is valid. Add more points for detail or submit the suggestion.';
        }
    };

    const clearMarkers = () => {
        if (!map) {
            return;
        }

        pointMarkers.forEach((marker) => {
            map.removeLayer(marker);
        });

        pointMarkers = [];
    };

    const render = (
        fit = false
    ) => {
        if (!map) {
            return;
        }

        if (polygon) {
            map.removeLayer(polygon);
            polygon = null;
        }

        clearMarkers();

        points.forEach((point, index) => {
            const marker = L.circleMarker(
                [
                    point.latitude,
                    point.longitude,
                ],
                {
                    radius: 6,
                    color: '#ffffff',
                    weight: 2,
                    fillColor: '#0f766e',
                    fillOpacity: 1,
                }
            )
                .addTo(map)
                .bindTooltip(
                    `Point ${index + 1}`,
                    {
                        direction: 'top',
                    }
                );

            pointMarkers.push(marker);
        });

        if (points.length >= 2) {
            polygon = L.polygon(
                points.map((point) => [
                    point.latitude,
                    point.longitude,
                ]),
                {
                    color: '#0f766e',
                    weight: 3,
                    fillColor: '#14b8a6',
                    fillOpacity:
                        points.length >= 3
                            ? 0.2
                            : 0.05,
                }
            ).addTo(map);
        }

        syncInput();
        updateStatus();

        if (
            fit
            && points.length > 0
        ) {
            const bounds =
                L.latLngBounds(
                    points.map((point) => [
                        point.latitude,
                        point.longitude,
                    ])
                );

            if (bounds.isValid()) {
                map.fitBounds(
                    bounds,
                    {
                        padding: [40, 40],
                        maxZoom: 18,
                        animate: false,
                    }
                );
            }
        }
    };

    const ensureMap = () => {
        if (map) {
            window.setTimeout(
                () => map.invalidateSize(),
                50
            );

            return;
        }

        const centerLatitude =
            parseCoordinate(
                picker.dataset.centerLatitude
            );

        const centerLongitude =
            parseCoordinate(
                picker.dataset.centerLongitude
            );

        map = L.map(
            mapElement,
            {
                zoomControl: true,
                scrollWheelZoom: false,
            }
        ).setView(
            [
                centerLatitude ?? 0,
                centerLongitude ?? 0,
            ],
            centerLatitude !== null
                && centerLongitude !== null
                ? 17
                : 2
        );

        L.tileLayer(
            'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
            {
                maxZoom: 20,
                attribution:
                    '&copy; OpenStreetMap contributors',
            }
        ).addTo(map);

        map.on(
            'click',
            (event) => {
                points.push({
                    latitude:
                        event.latlng.lat,
                    longitude:
                        event.latlng.lng,
                });

                render();
            }
        );

        render(
            points.length > 0
        );

        window.setTimeout(
            () => map.invalidateSize(),
            100
        );
    };

    const updateVisibility = () => {
        const visible =
            typeSelect.value
            === 'zone_correction'
            || typeSelect.value
                === 'new_zone';

        section.classList.toggle(
            'hidden',
            !visible
        );

        if (visible) {
            ensureMap();
        }
    };

    undoButton?.addEventListener(
        'click',
        () => {
            if (points.length === 0) {
                return;
            }

            points.pop();
            render();
        }
    );

    clearButton?.addEventListener(
        'click',
        () => {
            points = [];
            render();
        }
    );

    typeSelect.addEventListener(
        'change',
        updateVisibility
    );

    form.addEventListener(
        'submit',
        (event) => {
            const isZoneSuggestion =
                typeSelect.value
                === 'zone_correction'
                || typeSelect.value
                    === 'new_zone';

            if (! isZoneSuggestion) {
                boundaryInput.value = '';
                return;
            }

            if (
                typeSelect.value
                === 'new_zone'
                && points.length < 3
            ) {
                event.preventDefault();

                if (status) {
                    status.textContent =
                        'Draw at least three points to define the new zone before submitting.';
                }

                section.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });

                return;
            }

            if (
                points.length > 0
                && points.length < 3
            ) {
                event.preventDefault();

                if (status) {
                    status.textContent =
                        'Either clear the proposed boundary or draw at least three points before submitting.';
                }

                section.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });

                return;
            }

            syncInput();
        }
    );

    updateStatus();
    updateVisibility();
}

function initializeForms() {
    document
        .querySelectorAll(FORM_SELECTOR)
        .forEach(initializeForm);
}

if (document.readyState === 'loading') {
    document.addEventListener(
        'DOMContentLoaded',
        initializeForms,
        {
            once: true,
        }
    );
} else {
    initializeForms();
}

document.addEventListener(
    'livewire:navigated',
    initializeForms
);
